/* Data import wizard.
   Step 1 — pick kind (members | schemes | transactions). For transactions, also pick scheme.
   Step 2 — upload file → server returns auto-mapping + sample rows.
   Step 3 — review/edit mapping, commit. Server upserts by the kind's key field. */
const { useState: useStateI, useEffect: useEffectI, useRef: useRefI } = React;

const IMPORT_KINDS = [
  { kind: 'members',               title: 'Members',                  desc: 'Upserts by ITS No.' },
  { kind: 'schemes',               title: 'Schemes',                  desc: 'Upserts by Code.' },
  { kind: 'events',                title: 'Events',                   desc: 'Sit under a Scheme; resolved by Scheme Code.' },
  { kind: 'transactions',          title: 'Transactions (by scheme)', desc: 'Pick a scheme; rows imported under it.' },
  { kind: 'transactions_by_event', title: 'Transactions (by event)',  desc: 'Pick an event; bulk-load entries across schemes.' },
];

function ImportPage() {
  const [step, setStep] = useStateI(1);
  const [kind, setKind] = useStateI('members');
  const [schemeId, setSchemeId] = useStateI('');
  const [eventId, setEventId] = useStateI('');
  const [preview, setPreview] = useStateI(null);
  const [file, setFile] = useStateI(null);
  const [mapping, setMapping] = useStateI({});
  const [busy, setBusy] = useStateI(false);
  const [err, setErr] = useStateI('');
  const [result, setResult] = useStateI(null);
  const fileRef = useRefI(null);

  const reset = () => { setStep(1); setPreview(null); setFile(null); setMapping({}); setErr(''); setResult(null); };

  // Templates come in two flavours — Excel is the friendlier choice for users
  // copy-pasting from old spreadsheets; CSV stays available for ETL pipelines.
  const downloadTemplate = (format = 'xlsx') => {
    const ext = format === 'xlsx' ? 'xlsx' : 'csv';
    window.downloadCsv(`/import/${kind}/template?format=${format}`, `${kind}-template.${ext}`);
  };

  const onUpload = async (f) => {
    if (!f) return;
    setBusy(true); setErr(''); setFile(f);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await window.QH_API.postForm(`/import/${kind}/preview`, fd);
      setPreview(r);
      setMapping(r.suggestedMapping || {});
      setStep(3);
    } catch (e) { setErr(e.message || 'Upload failed.'); }
    finally { setBusy(false); }
  };

  const commit = async () => {
    if (!file || !preview) return;
    if (kind === 'transactions' && !schemeId) { setErr('Pick a scheme first.'); return; }
    // by-event picker is optional — when empty, each row resolves its own
    // event from the Event ID column (names aren't accepted here).
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mapping', JSON.stringify(mapping));
      if (kind === 'transactions') fd.append('schemeId', schemeId);
      if (kind === 'transactions_by_event') fd.append('eventId', eventId);
      const r = await window.QH_API.postForm(`/import/${kind}/commit`, fd);
      setResult(r);
      setStep(4);
      // Refresh the local store so the imported rows appear elsewhere.
      try { await window.QH_STORE.reload(); } catch {}
    } catch (e) { setErr(e.message || 'Import failed.'); }
    finally { setBusy(false); }
  };

  const requiredFields = (preview?.fields || []).filter(f => f.required);
  const allTargetsMapped = requiredFields.every(f => Object.values(mapping).includes(f.key));

  // After commit, the result table can have row-level errors. Re-upload the
  // source file along with the errors JSON so the server can pair each error
  // with its original column values and stream back an .xlsx.
  const exportErrors = async () => {
    if (!file || !result?.errors?.length) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('errors', JSON.stringify(result.errors));
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      await window.QH_API.postFormDownload(`/import/${kind}/errors-export`, fd, `${kind}-errors-${stamp}.xlsx`);
    } catch (e) { setErr(e.message || 'Export failed.'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Admin</span><I.ChevronRight s={10}/><span>Import</span></div>
          <h1>Data Import</h1>
          <div className="subtitle">Migrate Members, Schemes, and Transactions from your old system.</div>
        </div>
      </div>
      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:16}}>
        <div className="stepper">
          {['Pick kind', 'Upload file', 'Map columns', 'Result'].map((l, i) => (
            <React.Fragment key={i}>
              <div className={`step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                <span className="step-num">{step > i + 1 ? <I.Check s={11}/> : i + 1}</span>{l}
              </div>
              {i < 3 && <div className="step-line"/>}
            </React.Fragment>
          ))}
        </div>

        {step === 1 && (
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
            {IMPORT_KINDS.map(k => (
              <button key={k.kind} type="button" onClick={() => setKind(k.kind)}
                      className={`card ${kind === k.kind ? 'accent' : ''}`}
                      style={{textAlign:'left',padding:14,cursor:'pointer',border: kind === k.kind ? '2px solid var(--accent)' : '1px solid var(--line)'}}>
                <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{k.title}</div>
                <div className="text-xs muted">{k.desc}</div>
              </button>
            ))}
            {kind === 'transactions' && (
              <div className="field" style={{gridColumn:'1 / -1'}}>
                <label>Target scheme (transactions go under this scheme)</label>
                <select className="select" value={schemeId} onChange={e => setSchemeId(e.target.value)}>
                  <option value="">— Select scheme —</option>
                  {QH_DATA.schemes.map(s => <option key={s.id} value={s.id}>{s.name} · {s.code}</option>)}
                </select>
              </div>
            )}
            {kind === 'transactions_by_event' && (
              <div className="field" style={{gridColumn:'1 / -1'}}>
                <label>Target event (optional)</label>
                <select className="select" value={eventId} onChange={e => setEventId(e.target.value)}>
                  <option value="">— Per-row Event ID in the file —</option>
                  {(QH_DATA.events || []).map(ev => {
                    const sch = QH_DATA.schemes.find(s => s.id === ev.schemeId);
                    return <option key={ev.id} value={ev.id}>{ev.name} · {sch?.code || ev.schemeId} · {window.fmtDate(ev.date)}</option>;
                  })}
                </select>
                {eventId ? (() => {
                  const ev = QH_DATA.events.find(x => x.id === eventId);
                  const sch = ev && QH_DATA.schemes.find(s => s.id === ev.schemeId);
                  return ev ? <div className="text-xs muted" style={{marginTop:4}}>Every row inherits scheme <b>{sch?.name || ev.schemeId}</b>{ev.defaultBankName ? ` · default bank ${ev.defaultBankName}` : ''}</div> : null;
                })() : (
                  <div className="text-xs muted" style={{marginTop:4,lineHeight:1.5}}>
                    Pick one event above to apply to every row — <i>or</i> leave it on the default and include an
                    {' '}<span className="font-mono" style={{fontSize:11}}>Event ID</span> column in your file
                    so each row can target a different event (across schemes). Event names aren't supported here because they can collide.
                  </div>
                )}
              </div>
            )}
            <div style={{gridColumn:'1 / -1',display:'flex',gap:8,justifyContent:'flex-end',marginTop:8,flexWrap:'wrap'}}>
              <button className="btn" onClick={() => downloadTemplate('xlsx')}><I.Download/> Excel template</button>
              <button className="btn ghost" onClick={() => downloadTemplate('csv')}><I.Download/> CSV template</button>
              <button className="btn primary" onClick={() => setStep(2)}
                      disabled={kind === 'transactions' && !schemeId}>
                Continue<I.ArrowRight/>
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="card" style={{padding:24,textAlign:'center'}}>
            <div style={{fontSize:14,fontWeight:500,marginBottom:8}}>Upload your file</div>
            <div className="text-xs muted" style={{marginBottom:16}}>
              {IMPORT_KINDS.find(k => k.kind === kind).title}
              {kind === 'transactions' && schemeId ? ` · into ${QH_DATA.schemes.find(s => s.id === schemeId)?.name}` : ''}
              {kind === 'transactions_by_event' && eventId ? ` · into event ${QH_DATA.events.find(e => e.id === eventId)?.name}` : ''}
              <br/>Excel (.xlsx) and CSV are both accepted.
            </div>
            <input ref={fileRef} type="file"
                   accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                   style={{display:'none'}} onChange={e => onUpload(e.target.files[0])}/>
            <button className="btn primary" onClick={() => fileRef.current?.click()} disabled={busy}><I.Plus/> {busy ? 'Uploading…' : 'Choose file'}</button>
            <div className="text-xs muted" style={{marginTop:16}}>
              Don't have one yet?
              <button className="btn ghost sm" style={{marginLeft:8}} onClick={() => downloadTemplate('xlsx')}><I.Download s={11}/> Excel template</button>
              <button className="btn ghost sm" style={{marginLeft:4}} onClick={() => downloadTemplate('csv')}><I.Download s={11}/> CSV template</button>
            </div>
            {err && <div style={{color:'var(--neg)',fontSize:12,marginTop:12}}>{err}</div>}
            <div style={{marginTop:20}}>
              <button className="btn ghost" onClick={() => setStep(1)}>Back</button>
            </div>
          </div>
        )}

        {step === 3 && preview && (
          <div style={{display:'flex',flexDirection:'column',gap:12}}>
            <div style={{padding:12,background:'var(--info-soft)',color:'var(--info)',borderRadius:4,fontSize:12,display:'flex',alignItems:'center',gap:8}}>
              <I.AlertCircle s={13}/> {preview.totalRows} row(s) detected · {Object.keys(mapping).length} of {preview.headers.length} column(s) auto-mapped. {preview.notes}
            </div>

            <div className="card">
              <div className="card-header">Column mapping</div>
              <div className="card-body" style={{padding:0}}>
                <table className="data">
                  <thead><tr><th>File column</th><th>Maps to</th><th>Type</th><th>Required</th></tr></thead>
                  <tbody>
                    {preview.headers.map(h => {
                      const targetKey = mapping[h];
                      const f = preview.fields.find(f => f.key === targetKey);
                      // Other fields already used (so we can grey them out, except this row's current selection).
                      const taken = new Set(Object.entries(mapping).filter(([k]) => k !== h).map(([_, v]) => v));
                      return (
                        <tr key={h}>
                          <td className="font-mono text-xs">{h}</td>
                          <td>
                            <select className="select" style={{width:'100%'}} value={targetKey || ''}
                                    onChange={e => {
                                      const v = e.target.value;
                                      const next = {...mapping};
                                      if (!v) delete next[h]; else next[h] = v;
                                      setMapping(next);
                                    }}>
                              <option value="">— skip —</option>
                              {preview.fields.map(opt => (
                                <option key={opt.key} value={opt.key} disabled={taken.has(opt.key)}>
                                  {opt.label}{opt.required ? ' *' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="text-xs muted">{f?.type || '—'}</td>
                          <td>{f?.required ? <span className="tag warn">required</span> : <span className="text-xs muted">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {!allTargetsMapped && (
              <div style={{padding:10,background:'var(--neg-soft)',color:'var(--neg)',borderRadius:4,fontSize:12,display:'flex',alignItems:'center',gap:8}}>
                <I.AlertCircle s={12}/> Required fields are missing: {requiredFields.filter(f => !Object.values(mapping).includes(f.key)).map(f => f.label).join(', ')}
              </div>
            )}

            <div className="card">
              <div className="card-header">
                Preview (first {preview.sample.length} of {preview.totalRows})
                {(() => {
                  const bad = preview.sample.filter(r => r.cellErrors || r.error).length;
                  return bad > 0 ? <span className="tag neg" style={{marginLeft:8}}>{bad} row(s) with errors</span> : null;
                })()}
              </div>
              {preview.sample.some(r => r.cellErrors) && (
                <div style={{padding:'8px 12px',background:'var(--neg-soft)',color:'var(--neg)',fontSize:12,borderBottom:'1px solid var(--line)'}}>
                  <I.AlertCircle s={12}/> Cells highlighted red are invalid — hover for the rule. Fix in the source file and re-upload.
                </div>
              )}
              <div className="table-wrap" style={{border:'none',borderRadius:0,maxHeight:400,overflow:'auto'}}>
                <table className="data">
                  <thead><tr>
                    <th style={{width:60}}>#</th>
                    {preview.fields.filter(f => Object.values(mapping).includes(f.key)).map(f => (
                      <th key={f.key}>
                        {f.label}
                        {f.allowedValues?.length > 0 && (
                          <span className="text-xs muted" style={{marginLeft:4,fontWeight:400}}>({f.allowedValues.join(' / ')})</span>
                        )}
                        {!f.allowedValues?.length && f.type === 'bool' && (
                          <span className="text-xs muted" style={{marginLeft:4,fontWeight:400}}>(TRUE / FALSE)</span>
                        )}
                      </th>
                    ))}
                    <th>Status</th>
                  </tr></thead>
                  <tbody>
                    {preview.sample.map(r => (
                      <tr key={r.rowIndex}>
                        <td className="font-mono text-xs muted">{r.rowIndex + 2}</td>
                        {preview.fields.filter(f => Object.values(mapping).includes(f.key)).map(f => {
                          const cellErr = r.cellErrors?.[f.key];
                          const numericClass = (f.type === 'int' || f.type === 'decimal') ? 'font-mono text-xs' : '';
                          const cellStyle = cellErr
                            ? { background:'var(--neg-soft)', color:'var(--neg)', outline:'1px solid var(--neg)', fontWeight:500 }
                            : undefined;
                          return (
                            <td key={f.key} className={numericClass} style={cellStyle} title={cellErr || undefined}>
                              {r.values[f.key] ?? <span className="muted">—</span>}
                            </td>
                          );
                        })}
                        <td>{r.error
                          ? <span className="tag neg" title={r.error}><span className="dot"/>error</span>
                          : <span className="tag pos"><span className="dot"/>ok</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {err && <div style={{color:'var(--neg)',fontSize:12}}>{err}</div>}

            <div style={{display:'flex',gap:8}}>
              <button className="btn ghost" onClick={() => setStep(2)} disabled={busy}>Back</button>
              <div style={{flex:1}}/>
              <button className="btn accent" onClick={commit} disabled={busy || !allTargetsMapped}><I.Check/> {busy ? 'Importing…' : `Import ${preview.totalRows} row(s)`}</button>
            </div>
          </div>
        )}

        {step === 4 && result && (
          <div className="card" style={{padding:24}}>
            <div style={{fontSize:18,fontWeight:600,marginBottom:8,display:'flex',alignItems:'center',gap:8}}>
              <I.Check/> Import complete
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12,marginBottom:16}}>
              <KPIStrip items={[]}/>
              <div className="kpi"><div className="kpi-label">Created</div><div className="kpi-value pos">{result.created}</div></div>
              <div className="kpi"><div className="kpi-label">Updated</div><div className="kpi-value">{result.updated}</div></div>
              <div className="kpi"><div className="kpi-label">Skipped</div><div className="kpi-value muted">{result.skipped}</div></div>
              <div className="kpi"><div className="kpi-label">Errors</div><div className={`kpi-value ${result.errors.length ? 'neg' : ''}`}>{result.errors.length}</div></div>
            </div>
            {result.errors.length > 0 && (
              <>
                <div style={{display:'flex',alignItems:'center',marginBottom:6}}>
                  <div className="text-xs uppercase muted">{result.errors.length} row error{result.errors.length === 1 ? '' : 's'}</div>
                  <div className="grow"/>
                  <button className="btn sm" onClick={exportErrors} disabled={busy}>
                    <I.Download/> {busy ? 'Building…' : 'Export errors to Excel'}
                  </button>
                </div>
                <div className="table-wrap" style={{maxHeight:240,overflow:'auto'}}>
                  <table className="data">
                    <thead><tr><th>Row</th><th>Error</th></tr></thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i}><td className="font-mono text-xs">{e.row}</td><td className="text-xs">{e.message}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs muted" style={{marginTop:6,fontStyle:'italic'}}>
                  The export includes each error's row number, the error message, and the original column values from your uploaded file — fix and re-import.
                </div>
              </>
            )}
            <div style={{display:'flex',gap:8,marginTop:16,justifyContent:'flex-end'}}>
              <button className="btn" onClick={reset}>Import another file</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

window.ImportPage = ImportPage;
